import type React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Badge as ShadcnBadge } from "src/shadcn/components/badge";
import { cn } from "src/shadcn/lib/utils";

// Text colors are marked !important so Badge owns its color inside menu-style
// containers (DropdownMenu/Select/Menubar/Combobox items) whose focus state
// applies `**:text-accent-foreground` to all descendants.
const extendedBadgeVariants = cva("", {
  variants: {
    variant: {
      // --- Semantic (passthrough to shadcn — re-declare text color with !important to win over container cascades) ---
      default: "text-primary-foreground!",
      secondary: "text-secondary-foreground!",
      destructive: "text-destructive!",
      outline: "text-foreground!",
      ghost: "",
      link: "text-primary!",

      // --- Color ---
      gray: "bg-gray-200 text-gray-800! dark:bg-gray-700 dark:text-gray-100!",
      "gray-subtle":
        "bg-gray-100 text-gray-600! dark:bg-gray-800 dark:text-gray-300!",
      blue: "bg-blue-600 text-white! dark:bg-blue-500",
      "blue-subtle":
        "bg-blue-50 text-blue-700! dark:bg-blue-950/50 dark:text-blue-300!",
      purple: "bg-purple-700 text-white! dark:bg-purple-600",
      "purple-subtle":
        "bg-purple-50 text-purple-700! dark:bg-purple-950/50 dark:text-purple-300!",
      amber:
        "bg-amber-400 text-amber-950! dark:bg-amber-500 dark:text-amber-950!",
      "amber-subtle":
        "bg-amber-50 text-amber-700! dark:bg-amber-950/50 dark:text-amber-300!",
      red: "bg-red-600 text-white! dark:bg-red-500",
      "red-subtle":
        "bg-red-50 text-red-700! dark:bg-red-950/50 dark:text-red-300!",
      pink: "bg-pink-600 text-white! dark:bg-pink-500",
      "pink-subtle":
        "bg-pink-50 text-pink-700! dark:bg-pink-950/50 dark:text-pink-300!",
      green: "bg-green-600 text-white! dark:bg-green-500",
      "green-subtle":
        "bg-green-50 text-green-700! dark:bg-green-950/50 dark:text-green-300!",
      teal: "bg-teal-600 text-white! dark:bg-teal-500",
      "teal-subtle":
        "bg-teal-50 text-teal-700! dark:bg-teal-950/50 dark:text-teal-300!",
    },
    size: {
      sm: "h-4 px-1.5 py-0 text-[0.65rem] gap-0.5 [&>svg]:size-2.5!",
      default: "",
      lg: "h-6 px-3 py-0.5 text-sm gap-1.5 [&>svg]:size-4!",
    },
    shape: {
      pill: "",
      rounded: "rounded",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
    shape: "pill",
  },
});

// Semantic variants that map to shadcn's built-in variants
const SHADCN_VARIANTS = new Set([
  "default",
  "secondary",
  "destructive",
  "outline",
  "ghost",
  "link",
]);

type ExtendedBadgeVariant = NonNullable<
  VariantProps<typeof extendedBadgeVariants>["variant"]
>;
type ExtendedBadgeSize = NonNullable<
  VariantProps<typeof extendedBadgeVariants>["size"]
>;
type ExtendedBadgeShape = NonNullable<
  VariantProps<typeof extendedBadgeVariants>["shape"]
>;

interface BadgePrimitiveProps extends Omit<
  React.ComponentProps<typeof ShadcnBadge>,
  "variant"
> {
  variant?: ExtendedBadgeVariant;
  size?: ExtendedBadgeSize;
  shape?: ExtendedBadgeShape;
}

const Badge = ({
  variant = "default",
  size = "default",
  shape = "pill",
  className,
  ...props
}: BadgePrimitiveProps) => {
  // Pass semantic variants to shadcn, color variants override via className
  const shadcnVariant = SHADCN_VARIANTS.has(variant) ? variant : undefined;

  return (
    <ShadcnBadge
      variant={
        shadcnVariant as React.ComponentProps<typeof ShadcnBadge>["variant"]
      }
      className={cn(extendedBadgeVariants({ variant, size, shape }), className)}
      {...props}
    />
  );
};

export { Badge, extendedBadgeVariants as badgeVariants };
